import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    ArrowLeft, ShoppingCart, Coins, Star,
    Package, Check, ShieldCheck, Truck, Heart, Bell, Tote
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { StarRating } from '../components/shop/StarRating';
import { ProductReviews } from '../components/shop/ProductReviews';
import { ProductSizeGuide } from '../components/shop/ProductSizeGuide';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

export default function ProductDetail() {
    const { itemId } = useParams();
    const navigate = useNavigate();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [verifiedSellers, setVerifiedSellers] = useState([]);
    const [wishlisted, setWishlisted] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [selectedSize, setSelectedSize] = useState('');
    const [selectedColor, setSelectedColor] = useState('');
    const [purchasing, setPurchasing] = useState(false);
    const [addingToCart, setAddingToCart] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [reviewStats, setReviewStats] = useState({ avg_rating: 0, review_count: 0 });
    const [settingAlert, setSettingAlert] = useState(false);
    const [selectedImg, setSelectedImg] = useState(0);

    useEffect(() => {
        Promise.all([
            fetch(`${API}/marketplace/items/${itemId}`).then(r => r.ok ? r.json() : null),
            fetch(`${API}/connect/verified-sellers`).then(r => r.ok ? r.json() : []).catch(() => []),
            fetch(`${API}/reviews/${itemId}`).then(r => r.ok ? r.json() : { reviews: [], avg_rating: 0, review_count: 0 }).catch(() => ({ reviews: [], avg_rating: 0, review_count: 0 })),
            fetch(`${API}/wishlist/ids`, { credentials: 'include', headers: getH() }).then(r => r.ok ? r.json() : []).catch(() => []),
        ]).then(([prod, sellers, revData, wishIds]) => {
            setItem(prod);
            setVerifiedSellers(sellers);
            setReviews(revData.reviews || []);
            setReviewStats({ avg_rating: revData.avg_rating, review_count: revData.review_count });
            setWishlisted(wishIds.includes(itemId));
        }).finally(() => setLoading(false));
    }, [itemId]);

    const toggleWishlist = async () => {
        setToggling(true);
        try {
            const res = await fetch(`${API}/wishlist/${itemId}`, {
                method: 'POST', credentials: 'include', headers: getH(),
            });
            if (res.ok) {
                const data = await res.json();
                setWishlisted(data.action === 'added');
                toast.success(data.action === 'added' ? 'Added to wishlist' : 'Removed from wishlist');
            }
        } catch { toast.error('Failed'); }
        finally { setToggling(false); }
    };

    const setAlert = async (type) => {
        setSettingAlert(true);
        try {
            const res = await fetch(`${API}/alerts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({ item_id: itemId, type }),
            });
            if (res.ok) toast.success(`${type === 'price_drop' ? 'Price drop' : type === 'restock' ? 'Restock' : 'Update'} alert set!`);
            else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
        } catch { toast.error('Failed'); }
        finally { setSettingAlert(false); }
    };

    const handlePurchase = async () => {
        if (specs.sizes?.length > 0 && !selectedSize) { toast.error('Select a size'); return; }
        if (specs.colors?.length > 0 && !selectedColor) { toast.error('Select a color'); return; }
        setPurchasing(true);
        try {
            const res = await fetch(`${API}/marketplace/purchase`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({ item_id: itemId, quantity, size: selectedSize, color: selectedColor }),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`Purchased! -${data.total_cost} Boltz`);
                setItem(prev => ({ ...prev, stock: data.remaining_stock }));
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Purchase failed');
            }
        } catch { toast.error('Network error'); }
        finally { setPurchasing(false); }
    };

    const addToCart = async () => {
        if (specs.sizes?.length > 0 && !selectedSize) { toast.error('Select a size'); return; }
        if (specs.colors?.length > 0 && !selectedColor) { toast.error('Select a color'); return; }
        setAddingToCart(true);
        try {
            const res = await fetch(`${API}/cart/add`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({ item_id: itemId, quantity, size: selectedSize, color: selectedColor }),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`Added to cart (${data.cart_count} items)`);
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed to add');
            }
        } catch { toast.error('Network error'); }
        finally { setAddingToCart(false); }
    };

    const isVerified = item && verifiedSellers.some(
        s => s.display_name?.toLowerCase() === item.brand?.toLowerCase()
    );

    if (loading) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
                    <Skeleton className="h-8 w-48" />
                    <div className="grid md:grid-cols-2 gap-6">
                        <Skeleton className="aspect-square rounded-2xl" />
                        <div className="space-y-4">
                            <Skeleton className="h-10 w-full" />
                            <Skeleton className="h-20 w-full" />
                            <Skeleton className="h-12 w-full" />
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!item) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto px-4 py-6">
                    <Card className="glass-card"><CardContent className="p-8 text-center">
                        <Package size={48} className="text-muted-foreground mx-auto mb-4" />
                        <p className="font-medium text-foreground">Product not found</p>
                        <Button onClick={() => navigate('/shop')} className="mt-4" variant="outline">
                            <ArrowLeft size={16} className="mr-2" />Back to Shop
                        </Button>
                    </CardContent></Card>
                </div>
            </Layout>
        );
    }

    const specs = item.specs || {};
    const allImages = [item.image_url, ...(item.gallery_images || [])].filter(Boolean);

    return (
        <Layout>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
                <Button size="sm" variant="ghost" onClick={() => navigate('/shop')}
                    className="mb-4 text-muted-foreground" data-testid="back-to-shop">
                    <ArrowLeft size={16} className="mr-1" />Back to Shop
                </Button>

                <div className="grid md:grid-cols-2 gap-8">
                    {/* Image + Gallery */}
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                        <div className="aspect-square rounded-2xl overflow-hidden bg-muted relative" data-testid="product-image">
                            {allImages.length > 0 ? (
                                <img src={allImages[selectedImg] || allImages[0]} alt={item.name} className="w-full h-full object-cover transition-all" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <Package size={80} className="text-muted-foreground" />
                                </div>
                            )}
                            {item.is_featured && (
                                <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                                    <Star size={12} className="mr-1" weight="fill" />Featured
                                </Badge>
                            )}
                            <button onClick={toggleWishlist} disabled={toggling}
                                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center transition-transform hover:scale-110"
                                data-testid="wishlist-heart">
                                <Heart size={22} weight={wishlisted ? 'fill' : 'regular'}
                                    className={wishlisted ? 'text-plasma-red' : 'text-white'} />
                            </button>
                        </div>
                        {allImages.length > 1 && (
                            <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="gallery-thumbnails">
                                {allImages.map((img, i) => (
                                    <button key={i} onClick={() => setSelectedImg(i)}
                                        className={`w-16 h-16 rounded-lg overflow-hidden shrink-0 border-2 transition-all ${
                                            i === selectedImg ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'
                                        }`}>
                                        <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Info */}
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                {item.brand && <span className="text-xs text-primary font-display tracking-wider">{item.brand}</span>}
                                {isVerified && (
                                    <Badge className="text-[10px] bg-neon-green/10 text-neon-green border-neon-green/20" data-testid="verified-badge">
                                        <ShieldCheck size={10} className="mr-0.5" weight="fill" />Verified Seller
                                    </Badge>
                                )}
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-bold font-display text-foreground" data-testid="product-title">
                                {item.name}
                            </h1>
                            {reviewStats.review_count > 0 && (
                                <div className="flex items-center gap-2 mt-1.5">
                                    <StarRating rating={Math.round(reviewStats.avg_rating)} size={14} />
                                    <span className="text-xs text-muted-foreground">
                                        {reviewStats.avg_rating.toFixed(1)} ({reviewStats.review_count} review{reviewStats.review_count !== 1 ? 's' : ''})
                                    </span>
                                </div>
                            )}
                        </div>

                        {item.description && (
                            <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                        )}

                        {/* Price */}
                        <Card className="glass-card border-neon-gold/20">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-2xl font-black font-mono text-neon-gold" data-testid="product-price">
                                            {item.price_boltz?.toLocaleString()} <span className="text-base">Boltz</span>
                                        </p>
                                        {item.price_cad && (
                                            <p className="text-sm text-muted-foreground font-mono">
                                                ${item.price_cad?.toFixed(2)} CAD · ~{((item.price_boltz || 0) / 1000).toFixed(4)} SOL
                                            </p>
                                        )}
                                    </div>
                                    <div className="w-12 h-12 bg-neon-gold/10 rounded-full flex items-center justify-center">
                                        <Coins size={24} className="text-neon-gold" weight="fill" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Size Selector */}
                        {specs.sizes?.length > 0 && (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs text-muted-foreground font-display tracking-wider">SIZE</p>
                                    <ProductSizeGuide sizes={specs.sizes} selectedSize={selectedSize} />
                                </div>
                                <div className="flex flex-wrap gap-2" data-testid="size-selector">
                                    {specs.sizes.map(s => (
                                        <button key={s} onClick={() => setSelectedSize(selectedSize === s ? '' : s)}
                                            className={`px-3 py-1.5 text-xs font-mono rounded-lg border transition-all ${
                                                selectedSize === s
                                                    ? 'border-primary bg-primary/10 text-primary'
                                                    : 'border-border text-muted-foreground hover:border-foreground/20'
                                            }`} data-testid={`size-${s}`}>
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Color Selector */}
                        {specs.colors?.length > 0 && (
                            <div>
                                <p className="text-xs text-muted-foreground font-display tracking-wider mb-2">
                                    COLOR {selectedColor && <span className="text-foreground ml-1">— {selectedColor}</span>}
                                </p>
                                <div className="flex flex-wrap gap-2" data-testid="color-selector">
                                    {specs.colors.map(c => (
                                        <button key={c} onClick={() => setSelectedColor(selectedColor === c ? '' : c)}
                                            className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                                                selectedColor === c
                                                    ? 'border-primary bg-primary/10 text-primary font-bold'
                                                    : 'border-border text-muted-foreground hover:border-foreground/20'
                                            }`} data-testid={`color-${c.replace(/\s+/g, '-')}`}>
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Quantity + Buy / Cart */}
                        {item.stock > 0 ? (
                            <div className="space-y-2">
                                <div className="flex gap-3">
                                    <div className="flex items-center border border-border rounded-xl overflow-hidden">
                                        <button onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                            className="px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">−</button>
                                        <span className="px-4 py-2 font-mono text-sm text-foreground" data-testid="quantity">{quantity}</span>
                                        <button onClick={() => setQuantity(q => Math.min(item.stock || 10, q + 1))}
                                            className="px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">+</button>
                                    </div>
                                    <Button className="flex-1 btn-primary" data-testid="buy-now-btn"
                                        onClick={handlePurchase} disabled={purchasing}>
                                        <ShoppingCart size={18} className="mr-2" />{purchasing ? 'Processing...' : 'Buy Now'}
                                    </Button>
                                </div>
                                <Button variant="outline" className="w-full" data-testid="add-to-cart-btn"
                                    onClick={addToCart} disabled={addingToCart}>
                                    <Tote size={18} className="mr-2" />{addingToCart ? 'Adding...' : 'Add to Cart'}
                                </Button>
                            </div>
                        ) : (
                            <div className="p-4 rounded-xl bg-plasma-red/5 border border-plasma-red/20 text-center" data-testid="sold-out-banner">
                                <p className="text-plasma-red font-display font-bold">SOLD OUT</p>
                                <p className="text-xs text-muted-foreground mt-1">Set a restock alert to be notified when it&apos;s back</p>
                            </div>
                        )}

                        {/* Stock & Shipping */}
                        <div className="flex gap-4 text-xs text-muted-foreground">
                            {item.stock > 0 && (
                                <span className="flex items-center gap-1"><Check size={12} className="text-neon-green" />{item.stock} in stock</span>
                            )}
                            {item.dropship_source && (
                                <span className="flex items-center gap-1"><Truck size={12} />Ships from supplier</span>
                            )}
                        </div>

                        {/* Price Alert */}
                        <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setAlert('price_drop')} disabled={settingAlert}
                                className="flex-1 text-xs" data-testid="alert-price-drop">
                                <Bell size={14} className="mr-1" />Price Drop Alert
                            </Button>
                            {item.stock === 0 && (
                                <Button size="sm" variant="outline" onClick={() => setAlert('restock')} disabled={settingAlert}
                                    className="flex-1 text-xs" data-testid="alert-restock">
                                    <Package size={14} className="mr-1" />Restock Alert
                                </Button>
                            )}
                        </div>

                        {/* Specs */}
                        {Object.keys(specs).length > 0 && (
                            <Card className="glass-card">
                                <CardContent className="p-4">
                                    <h3 className="font-display text-xs text-muted-foreground tracking-wider mb-3">SPECIFICATIONS</h3>
                                    <div className="space-y-2">
                                        {Object.entries(specs).map(([key, val]) => (
                                            <div key={key} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-0">
                                                <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                                                <span className="font-mono text-foreground">{typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        <div className="flex flex-wrap gap-2">
                            {item.category && <Badge variant="outline" className="text-xs">{item.category}</Badge>}
                            {item.subcategory && <Badge variant="outline" className="text-xs">{item.subcategory}</Badge>}
                        </div>
                    </motion.div>
                </div>

                {/* Reviews Section */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <ProductReviews
                        itemId={itemId}
                        reviews={reviews} setReviews={setReviews}
                        reviewStats={reviewStats} setReviewStats={setReviewStats}
                    />
                </motion.div>
            </div>
        </Layout>
    );
}
